<template>
	<div>
		<BaseHeader :title="$t('broker_case').toString()">
			<div class="pr-8 font-bold">Status: {{ getBrokerCaseStatus(brokerCase.status) ?? '-' }}</div>
		</BaseHeader>
		<div class="main" v-if="!isLoading">
			<CardWrapper class="main">
				<BaseCard :is-editable="false" :title="$t('customer').toString()" :is-toggleable="false">
					<template #icons>
						<slot name="icons" />
					</template>
					<div v-if="brokerCase">
						<div class="grid grid-cols-2 max-w-full w-full">
							<div class="font-bold">{{ $t('name') }}</div>
							<div class="break-words">{{ getNameLabel }}</div>
						</div>
						<div class="grid grid-cols-2 max-w-full w-full">
							<div class="font-bold">{{ $t('address') }}</div>
							<div class="break-words" style="white-space: pre-line">{{ getStreetLabel }}</div>
						</div>
						<div class="grid grid-cols-2 max-w-full w-full pt-4">
							<div class="font-bold">{{ $t('phone') }}</div>
							<div class="break-words" style="white-space: pre-line">{{ brokerCase.phone ?? '-' }}</div>
						</div>
						<div class="grid grid-cols-2 max-w-full w-full">
							<div class="font-bold">{{ $t('e_mail') }}</div>
							<div class="break-words" style="white-space: pre-line">{{ brokerCase.email ?? '-' }}</div>
						</div>
					</div>
				</BaseCard>
				<BaseCard :is-editable="false" :title="$t('accident_data').toString()" :is-toggleable="false">
					<template #icons>
						<slot name="icons" />
					</template>
					<div v-if="brokerCase">
						<div class="grid grid-cols-2 max-w-full w-full">
							<div class="font-bold">{{ $t('license_plate_opponent') }}</div>
							<div class="break-words">{{ brokerCase.causer_license_plate ?? '-' }}</div>
						</div>
						<div class="grid grid-cols-2 max-w-full w-full">
							<div class="font-bold">{{ $t('opposite_insurance_name') }}</div>
							<div class="break-words">{{ getOppositeInsuranceName }}</div>
						</div>

						<div class="grid grid-cols-2 max-w-full w-full">
							<div class="font-bold">{{ $t('opposite_insurance_number') }}</div>
							<div class="break-words">{{ getOppositeInsuranceNumber }}</div>
						</div>

						<div class="grid grid-cols-2 max-w-full w-full pb-4">
							<div class="font-bold">{{ $t('opposite_insurance_case_number') }}</div>
							<div class="break-words">{{ getOppositeInsuranceCaseNumber }}</div>
						</div>

						<div class="grid grid-cols-2 max-w-full w-full">
							<div class="font-bold">{{ $t('accident_date') }}</div>
							<div class="break-words">{{ getAccidentDate }}</div>
						</div>
						<div class="grid grid-cols-2 max-w-full w-full">
							<div class="font-bold">{{ $t('note') }}</div>
							<div class="break-words">{{ brokerCase.note ?? '-' }}</div>
						</div>
						<div class="grid grid-cols-2 max-w-full w-full">
							<div class="font-bold">{{ $t('accident_document') }}</div>
							<div class="h-5 text-sm flex items-center text-connectProBlue cursor-pointer" v-if="accidentReport" @click="getAccidentDocument()">
								<font-awesome-icon :icon="['fas', 'download']"></font-awesome-icon>
							</div>
							<div v-else>
								-
							</div>
						</div>
					</div>
				</BaseCard>
				<BaseCard :is-editable="false" :title="$t('vehicle').toString()" :is-toggleable="false" v-if="vehicleRegistrationDocument">
					<template #icons>
						<slot name="icons" />
					</template>
					<div v-if="brokerCase">
						<div class="grid grid-cols-2 max-w-full w-full">
							<div class="font-bold">{{ $t('vehicle_registration') }}</div>
							<div
								class="h-5 text-sm flex items-center text-connectProBlue cursor-pointer"
								v-if="vehicleRegistrationDocument"
								@click="getVehicleRegistrationDocument()"
							>
								<font-awesome-icon :icon="['fas', 'download']"></font-awesome-icon>
							</div>
							<div v-else>
								-
							</div>
						</div>
					</div>
				</BaseCard>
				<BaseCard :is-editable="false" :title="$t('lawyers_office').toString()" :is-toggleable="false" v-if="brokerCase.lawyer_contact">
					<template #icons>
						<slot name="icons" />
					</template>
					<div v-if="brokerCase.lawyer_contact">
						<div class="grid grid-cols-2 max-w-full w-full">
							<div class="font-bold">{{ $t('name') }}</div>
							<div class="break-words">{{ brokerCase.lawyer_contact.company_name }}</div>
						</div>
						<div class="grid grid-cols-2 max-w-full w-full" v-if="brokerCase.lawyer_contact.contact_name">
							<div class="font-bold">{{ $t('contact_person') }}</div>
							<div class="break-words" style="white-space: pre-line">{{ brokerCase.lawyer_contact.contact_name }}</div>
						</div>
						<div class="grid grid-cols-2 max-w-full w-full">
							<div class="font-bold">{{ $t('address') }}</div>
							<div class="break-words" style="white-space: pre-line">{{ getStreetLabelLawyerContact }}</div>
						</div>
						<div class="grid grid-cols-2 max-w-full w-full pt-4">
							<div class="font-bold">{{ $t('e_mail') }}</div>
							<div class="break-words" style="white-space: pre-line">{{ brokerCase.lawyer_contact.email ?? '-' }}</div>
						</div>
					</div>
				</BaseCard>
				<BaseCard :is-editable="false" :title="$t('photos').toString()" :is-toggleable="false">
					<div class="-mr-2" v-if="imageMedia.length > 0">
						<CoolLightBox :items="getLightBoxImages" :index="index" @close="index = null"> </CoolLightBox>
						<div class="flex w-full flex-wrap justify-between">
							<div class="w-1/4 mr-2 pb-2 box-border flex-grow" v-for="(metaInfoImage, metaInfoImageIndex) in imageMedia" :key="metaInfoImageIndex">
								<img
									:src="metaInfoImage.base64content"
									:alt="'photo' + index"
									@click="index = metaInfoImageIndex"
									class="w-full bg-cover bg-center flex flex-grow cursor-pointer"
								/>
							</div>
						</div>
					</div>
					<div class="font-bold" v-else>{{ $t('no_images_transferred') }}</div>
				</BaseCard>
			</CardWrapper>
		</div>
		<LoadingSpinner v-else />
		<Footer @backButtonClick="goBack()" :is-continue-button="brokerCase.status === 'new'" :is-skippable="false" :is-back-button="true" class="m-8">
			<template #right v-if="brokerCase.status === 'new'">
				<BaseButton @click="declineCase()" class="btn-default mr-6">{{ $t('decline') }}</BaseButton>
			</template>
			<template #continueButton v-if="brokerCase.status === 'new'">
				<BaseButton @click="assignMe()" class="btn-blue">{{ $t('assign_me') }}</BaseButton>
			</template>
		</Footer>
	</div>
</template>

<script>
import BaseHeader from '@/components/navigation/BaseHeader';
import Formatter from '@/mixins/Formatter';
import DunningStatus from '@/mixins/damagecase/DunningStatus';
import Authentication from '@/mixins/Authentication';
import DamageAssessorCaseRepository from '@/api/repositories/DamageAssessorCaseRepository';
import Footer from '@/components/damagecases/Footer.vue';
import BaseButton from '@/components/forms/buttons/BaseButton.vue';
import router from '@/router';
import BaseCard from '@/components/damagecases/forms/BaseCard.vue';
import LoadingSpinner from '@/components/navigation/LoadingSpinner.vue';
import CardWrapper from '@/components/damagecases/forms/CardWrapper.vue';
import { concatArray, getGermanDate, getTimeWithLeadingNull } from '@/plugins/Utils';
import { getBrokerCaseStatus } from '@/helpers/Utils';

export default {
	name: 'BrokerCaseDetail',
	components: {
		CardWrapper,
		LoadingSpinner,
		BaseCard,
		BaseButton,
		Footer,
		BaseHeader,
	},
	mixins: [Formatter, DunningStatus, Authentication],
	data() {
		return {
			isLoading: false,
			brokerCase: { city: {}, media: [], lawyer_contact: {} },
			index: null,
			accidentReport: null,
			vehicleRegistrationDocument: null,
			imageMedia: [],
		};
	},
	mounted() {
		this.isLoading = true;
		this.getCaseById();
	},
	computed: {
		getLightBoxImages() {
			return this.imageMedia
				.map(metaInfo => {
					if (metaInfo.base64content) {
						return metaInfo.base64content;
					}
				})
				.filter(value => value);
		},
		getNameLabel() {
			if (this.brokerCase.first_name) {
				return this.brokerCase.first_name + ' ' + this.brokerCase.last_name;
			} else {
				return '';
			}
		},
		getStreetLabel() {
			let label = this.brokerCase.street;
			if (this.brokerCase.house_number) {
				label = label + ' ' + this.brokerCase.house_number;
			}
			label = label + '\n' + concatArray([this.brokerCase.city.zip_code, this.brokerCase.city.name]);
			return label;
		},
		getStreetLabelLawyerContact() {
			let label = this.brokerCase.lawyer_contact.street;
			if (this.brokerCase.lawyer_contact.house_number) {
				label = label + ' ' + this.brokerCase.lawyer_contact.house_number;
			}
			label = label + '\n' + concatArray([this.brokerCase.lawyer_contact.city.zip_code, this.brokerCase.lawyer_contact.city.name]);
			return label;
		},
		getAccidentDate() {
			if (!this.brokerCase.date_of_accident) {
				return '-';
			}

			const date = new Date(this.brokerCase.date_of_accident * 1000);
			const dateStr = getGermanDate(date);

			if (this.brokerCase.damage_time_set_by_user) {
				const hours = String(date.getHours()).padStart(2, '0');
				const minutes = String(date.getMinutes()).padStart(2, '0');
				return `${dateStr} - ${hours}:${minutes} Uhr`;
			}

			return dateStr;
		},

		getOppositeInsuranceName() {
			if (!this.brokerCase.opposite_insurance_name) {
				return '-';
			}
			return this.brokerCase.opposite_insurance_name;
		},

		getOppositeInsuranceNumber() {
			if (!this.brokerCase.opposite_insurance_number) {
				return '-';
			}
			return this.brokerCase.opposite_insurance_number;
		},

		getOppositeInsuranceCaseNumber() {
			if (!this.brokerCase.opposite_insurance_case_number) {
				return '-';
			}
			return this.brokerCase.opposite_insurance_case_number;
		},
	},
	methods: {
		getBrokerCaseStatus,
		router() {
			return router;
		},
		getCaseById() {
			new DamageAssessorCaseRepository()
				.getBrokerCaseDetail(this.$route.params.id)
				.then(response => {
					this.brokerCase = response.data;
					if (this.brokerCase.media) {
						this.accidentReport = this.brokerCase.media.find(element => element.category === 'accident_report');
						this.vehicleRegistrationDocument = this.brokerCase.media.find(element => element.category === 'vehicle_registration_document');
						this.imageMedia = this.brokerCase.media.filter(element => element.category === null);
					}
					this.isLoading = false;
				})
				.catch(e => {
					this.isLoading = false;
					console.log(e);
				});
		},
		assignMe() {
			if (this.brokerCase.status !== 'new') {
				return;
			}
			new DamageAssessorCaseRepository()
				.claimBrokerCase(this.$route.params.id)
				.then(response => {
					this.$swal(this.$t('general.success').toString(), this.$t('case_assigned_successfully').toString(), 'success').then(_ => {
						this.router().push({ name: 'damageAssessorCaseDetail', params: { id: response.data.id } });
					});
				})
				.catch(e => {
					this.$swal(this.$t('general.error').toString(), this.$t('case_cannot_be_assigned').toString(), 'error');
				});
		},
		declineCase() {
			if (this.brokerCase.status !== 'new') {
				return;
			}
			new DamageAssessorCaseRepository()
				.closeBrokerCase(this.$route.params.id)
				.then(response => {
					this.$swal(this.$t('general.success').toString(), this.$t('case_declined').toString(), 'success').then(_ => {
						this.$router.push({ name: 'brokerCaseOverview' });
					});
				})
				.catch(e => {
					this.$swal(this.$t('general.error').toString(), this.$t('compensation_overview').toString(), 'error');
				});
		},
		goBack() {
			return this.$router.push({ name: 'brokerCaseOverview' });
		},
		getFileType(type) {
			switch (type) {
				case 'png':
					return 'image/png';
				case 'pdf':
					return 'application/pdf';
				case 'jpg':
				case 'jpeg':
					return 'image/jpg';
			}
		},
		getAccidentDocument() {
			const blob = new Blob([this.base64ToArrayBuffer(this.accidentReport.base64content.split(',')[1])], {
				type: this.getFileType(this.accidentReport.extension),
			});
			const name = 'Unfallbericht.' + this.accidentReport.extension;
			const resolvingUrl = URL.createObjectURL(blob);
			setTimeout(function() {
				const a = document.createElement('a');
				a.style.display = 'none';
				a.href = resolvingUrl;
				a.download = name;
				document.body.appendChild(a);
				a.click();
				window.URL.revokeObjectURL(resolvingUrl);
			}, 200);
		},
		getVehicleRegistrationDocument() {
			const blob = new Blob([this.base64ToArrayBuffer(this.vehicleRegistrationDocument.base64content.split(',')[1])], {
				type: this.getFileType(this.vehicleRegistrationDocument.extension),
			});
			const name = 'Fahrzeugschein.' + this.vehicleRegistrationDocument.extension;
			const resolvingUrl = URL.createObjectURL(blob);
			setTimeout(function() {
				const a = document.createElement('a');
				a.style.display = 'none';
				a.href = resolvingUrl;
				a.download = name;
				document.body.appendChild(a);
				a.click();
				window.URL.revokeObjectURL(resolvingUrl);
			}, 200);
		},
		base64ToArrayBuffer(base64) {
			const binaryString = atob(base64);
			const bytes = new Uint8Array(binaryString.length);
			for (let i = 0; i < binaryString.length; i++) {
				bytes[i] = binaryString.charCodeAt(i);
			}
			return bytes.buffer;
		},
	},
};
</script>
